import React, { useState, useMemo, useEffect } from "react";
import { Hooks, Util, Context, Request, Storage, Cookie } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { XtContext } = Context;
const { getUrl, Big, getOrigin } = Util;
const { XtAxios4 } = Request;
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { get_balanceTotalWithAiFollow, get_priceCurrencyConvert } from "@/api/v4/balance";
import { observer } from "mobx-react-lite";
import styles from "./index.module.less";
import { useStore } from "@/models";
import { getRecommendedFiatCodesFromList } from "@/utils/fiatMarket";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler
);

// 眼睛图标 SVG
const EyeIcon = ({ isOpen, onClick }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    onClick={onClick}
    className={styles["eye-icon"]}
  >
    {isOpen ? (
      // 非脱敏（显示）状态
      <path
        d="M8.00001 3.27193C4.84795 3.27193 2.15611 5.2325 1.06549 8C2.1561 10.7675 4.84795 12.7281 8.00001 12.7281C11.1552 12.7281 13.8439 10.7675 14.9345 8C13.8439 5.2325 11.1552 3.27193 8.00001 3.27193ZM8.00001 11.1521C6.26008 11.1521 4.84795 9.73994 4.84795 8C4.84795 6.26006 6.26007 4.84795 8.00001 4.84795C9.73995 4.84795 11.1521 6.26006 11.1521 8C11.1521 9.73994 9.73993 11.1521 8.00001 11.1521ZM8.00001 6.10877C6.95668 6.10877 6.10878 6.95668 6.10878 8C6.10878 9.04333 6.95668 9.89123 8.00001 9.89123C9.04333 9.89123 9.89123 9.04333 9.89123 8C9.89123 6.95668 9.04333 6.10877 8.00001 6.10877Z"
        fill="white"
        fillOpacity="0.4"
      />
    ) : (
      // 已脱敏（隐藏）状态
      <>
        <rect
          x="13.4006"
          y="2.46928"
          width="1.71726"
          height="13.926"
          transform="rotate(45 13.4006 2.46928)"
          fill="white"
          fillOpacity="0.4"
        />
        <path
          d="M13.5391 5.22269C14.1289 5.87961 14.6051 6.64162 14.9346 7.47758C13.8439 10.2449 11.1551 12.2051 8 12.2051C7.54792 12.2051 7.10556 12.1632 6.67578 12.086L8.13672 10.625C9.76775 10.5553 11.0775 9.24529 11.1475 7.6143L13.5391 5.22269ZM8 2.74906C9.46169 2.74907 10.8227 3.17066 11.9717 3.8975L10.415 5.45414C9.83669 4.76456 8.96974 4.32525 8 4.32523C6.26006 4.32523 4.84766 5.73764 4.84766 7.47758C4.84779 8.44714 5.28709 9.31435 5.97656 9.89262L4.52539 11.3438C2.95839 10.5142 1.72169 9.14263 1.06543 7.47758C2.15605 4.71008 4.84794 2.74906 8 2.74906ZM8 5.58598C8.62136 5.58599 9.17171 5.88793 9.5166 6.3516L6.875 8.99418C6.41111 8.64939 6.10854 8.09903 6.1084 7.47758C6.1084 6.43425 6.95668 5.58598 8 5.58598Z"
          fill="white"
          fillOpacity="0.4"
        />
      </>
    )}
  </svg>
);

// 展开/收起图标
const ExpandIcon = ({ expanded, onClick }) => (
  <svg
    width="10"
    height="7"
    viewBox="0 0 10 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    onClick={onClick}
    className={`${styles["expand-icon"]} ${expanded ? styles["expand-icon--expanded"] : ""}`}
  >
    <path
      d="M5 6.24001L5.72394e-08 1.44001L1.25 0.240006L5 3.84001L8.75 0.240006L10 1.44001L5 6.24001Z"
      fill="white"
    />
  </svg>
);

const AssetBalance = ({
  isLogin,
  isAuthChecking = false,
  chartData: propChartData,
  chartExpanded,
  onToggleChartExpand,
  onOpenDeposit,
  /** 定时拉取总余额后，若与上次轮询结果不同则回调（供首页任务进度随资产变化刷新） */
  onTotalUsdtAmountChanged,
}) => {
  const t = useTranslation();
  const { fund } = useStore();
  const [appState, appDispatch] = React.useContext(XtContext);
  const [hideBalance, setHideBalance] = useState(false);
  const [totalUsdtAmount, setTotalUsdtAmount] = useState("0");
  const [convertedAmount, setConvertedAmount] = useState("0");
  const [exchangeRate, setExchangeRate] = useState(null);
  const [usdRate, setUsdRate] = useState(null); // USDT -> USD，用于下方固定展示 USD 金额与 $ 符号
  // 余额法币选择：USDT 为基础币，选中的为展示法币
  const [fiatOptions, setFiatOptions] = useState(["USDT"]); // 可选法币列表，始终包含 USDT
  const [selectedFiat, setSelectedFiat] = useState("USDT"); // 当前选中的法币
  const [fiatDropdownOpen, setFiatDropdownOpen] = useState(false);
  const [fiatSelectable, setFiatSelectable] = useState(false); // 是否允许切换法币
  /** 用户是否在本页下拉中选过法币（选过后优先保留，不因 effect 重跑被 primaryFiat 覆盖） */
  const userHasChosenFiatRef = React.useRef(false);
  /** 法币下拉容器，用于点击外部关闭 */
  const fiatDropdownWrapRef = React.useRef(null);
  /** 上次成功拉取到的总余额（USDT 字符串），用于轮询检测是否变化 */
  const lastPolledTotalUsdtRef = React.useRef(null);
  const onTotalUsdtAmountChangedRef = React.useRef(onTotalUsdtAmountChanged);
  React.useEffect(() => {
    onTotalUsdtAmountChangedRef.current = onTotalUsdtAmountChanged;
  }, [onTotalUsdtAmountChanged]);
  const recommendedFiatCodes = useMemo(
    () => getRecommendedFiatCodesFromList(fund?.recommendedCurrencyList),
    // 不能只看 version：IP/策略变化时列表可能已更新但 version 字符串未变，会导致下拉仍用旧法币
    [fund?.recommendedCurrencyList, fund?.recommendedCurrencyListMd5]
  );
  const recommendedFiatCodesKey = recommendedFiatCodes.join(",");

  /** 点击打开与 QuickActions 相同的底部充值弹层 */
  const depositLabel = t("home.assetBalanceDepositButton") || "Deposit";

  // 法币下拉：点击页面其他区域关闭
  useEffect(() => {
    if (!fiatDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (fiatDropdownWrapRef.current && !fiatDropdownWrapRef.current.contains(e.target)) {
        setFiatDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [fiatDropdownOpen]);

  // 获取资产总额
  const fetchBalanceTotal = async () => {
    if (!isLogin) return;
    try {
      const res = await get_balanceTotalWithAiFollow();
      const nextRaw =
        res && res.totalUsdtAmount != null && String(res.totalUsdtAmount).trim() !== ""
          ? String(res.totalUsdtAmount)
          : "0";
      const prevPoll = lastPolledTotalUsdtRef.current;
      lastPolledTotalUsdtRef.current = nextRaw;
      setTotalUsdtAmount(nextRaw);
      const cb = onTotalUsdtAmountChangedRef.current;
      if (typeof cb === "function" && prevPoll !== null && prevPoll !== nextRaw) {
        cb(nextRaw, prevPoll);
      }
    } catch (error) {
      console.error("Failed to load balance total:", error);
      const nextRaw = "0";
      const prevPoll = lastPolledTotalUsdtRef.current;
      lastPolledTotalUsdtRef.current = nextRaw;
      setTotalUsdtAmount("0");
      const cb = onTotalUsdtAmountChangedRef.current;
      if (typeof cb === "function" && prevPoll !== null && prevPoll !== nextRaw) {
        cb(nextRaw, prevPoll);
      }
    }
  };

  /**
   * 解析 convert 接口返回的汇率，得到「1 USDT = rate 法币」用于展示。
   * 接口可能返回 data.usdt.aed 或 data.aed.usdt，key 可能大小写混用。
   */
  const parseUsdtToFiatRate = (data, targetCurrencyLower) => {
    if (!data || !targetCurrencyLower) return null;
    const tc = targetCurrencyLower;
    const tcUpper = tc.toUpperCase();
    // 1 USDT = x 法币：直接取
    const fromUsdt = (data.usdt || data.USDT || {})[tc] ?? (data.usdt || data.USDT || {})[tcUpper];
    if (fromUsdt != null && Number(fromUsdt) > 0) return parseFloat(fromUsdt);
    // 1 法币 = x USDT：取倒数
    const quote = data[tc] || data[tcUpper];
    const toUsdt = quote && (quote.usdt ?? quote.USDT);
    if (toUsdt != null && Number(toUsdt) > 0) return 1 / parseFloat(toUsdt);
    return null;
  };

  // 获取选中法币的汇率（用于主金额区切换汇率时显示）
  const fetchExchangeRate = async () => {
    if (!isLogin || !selectedFiat) return;
    try {
      const targetCurrency = (selectedFiat || "USDT").toLowerCase();
      if (targetCurrency === "usdt") {
        setExchangeRate(1);
        return;
      }
      const res = await get_priceCurrencyConvert({
        params: {
          converts: `usdt,${targetCurrency}`,
        },
      });
      const data = res?.result || res?.data || res;
      const rate = parseUsdtToFiatRate(data, targetCurrency);
      setExchangeRate(rate);
    } catch (error) {
      console.error("Failed to load exchange rate:", error);
      setExchangeRate(null);
    }
  };

  // 获取 USDT -> USD 汇率（用于下方固定展示 ≈$ 金额）
  const fetchUsdRate = async () => {
    if (!isLogin) return;
    try {
      const res = await get_priceCurrencyConvert({
        params: { converts: "usdt,usd" },
      });
      const data = res?.result || res?.data || res;
      const rate = parseUsdtToFiatRate(data, "usd");
      setUsdRate(rate != null ? rate : 1);
    } catch (error) {
      setUsdRate(1);
    }
  };

  // 计算转换后的金额
  useEffect(() => {
    if (totalUsdtAmount && exchangeRate !== null) {
      const usdtAmount = Big(totalUsdtAmount || "0");
      const converted = selectedFiat?.toLowerCase() === "usdt" 
        ? usdtAmount 
        : usdtAmount.times(exchangeRate || 1);
      setConvertedAmount(converted.toFixedCy(2));
    } else if (totalUsdtAmount && selectedFiat?.toLowerCase() === "usdt") {
      setConvertedAmount(Big(totalUsdtAmount || "0").toFixedCy(2));
    } else {
      setConvertedAmount("0");
    }
  }, [totalUsdtAmount, exchangeRate, selectedFiat]);

  // 获取资产总额
  useEffect(() => {
    if (isLogin) {
      fetchBalanceTotal();
      // 每5秒刷新一次
      const timer = setInterval(() => {
        fetchBalanceTotal();
      }, 5000);
      return () => clearInterval(timer);
    } else {
      lastPolledTotalUsdtRef.current = null;
      setTotalUsdtAmount("0");
      setConvertedAmount("0");
    }
  }, [isLogin]);

  // 获取选中法币汇率
  useEffect(() => {
    if (isLogin && selectedFiat) {
      fetchExchangeRate();
      const timer = setInterval(() => fetchExchangeRate(), 30000);
      return () => clearInterval(timer);
    } else {
      setExchangeRate(null);
    }
  }, [isLogin, selectedFiat]);

  // 获取 USD 汇率（下方固定展示 ≈$ 用）
  useEffect(() => {
    if (isLogin) {
      fetchUsdRate();
      const timer = setInterval(() => fetchUsdRate(), 30000);
      return () => clearInterval(timer);
    } else {
      setUsdRate(null);
    }
  }, [isLogin]);

  // 资产页法币展示直接跟随推荐币种缓存，避免再走本地余额/IP 二次判断。
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const options = ["USDT", ...recommendedFiatCodes];
      const primaryFiat = recommendedFiatCodes[0] || "USDT";
      const selectable = recommendedFiatCodes.length > 0;

      if (!isLogin) {
        if (cancelled) return;
        setFiatOptions((prev) => {
          const prevKey = Array.isArray(prev) ? prev.join(",") : "";
          const nextKey = options.join(",");
          return prevKey === nextKey ? prev : options;
        });
        setSelectedFiat((prev) => (prev === "USDT" ? prev : "USDT"));
        setFiatSelectable((prev) => (prev === selectable ? prev : selectable));
        return;
      }

      if (cancelled) return;

      setFiatOptions((prev) => {
        const prevKey = Array.isArray(prev) ? prev.join(",") : "";
        const nextKey = options.join(",");
        return prevKey === nextKey ? prev : options;
      });
      // 默认选中：用户在本页下拉选过则保留当前；否则优先 primaryFiat（余额/充值/IP），再 cookie，再兜底
      const globalCode = (Cookie.get("currency") || appState?.currency || "").toUpperCase() || null;
      setSelectedFiat((prev) => {
        if (userHasChosenFiatRef.current && prev && options.includes(prev)) return prev;
        if (primaryFiat && options.includes(primaryFiat)) return primaryFiat;
        if (globalCode && options.includes(globalCode)) return globalCode;
        if (prev && options.includes(prev)) return prev;
        return primaryFiat || options[0];
      });
      setFiatSelectable((prev) => (prev === selectable ? prev : selectable));
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [appState?.currency, isLogin, recommendedFiatCodesKey]);

  // 首页 Total Balance 选中的法币需同步到全局 currency，供 Last Price 等模块与 fund.assist 一致
  useEffect(() => {
    if (!isLogin || !selectedFiat) return;
    const lowerKey = String(selectedFiat).toLowerCase();
    const current = String(appState?.currency || "").toLowerCase();
    if (!lowerKey || lowerKey === current) return;
    Cookie.set("currency", lowerKey);
    appDispatch({
      payload: {
        currency: lowerKey,
      },
    });
  }, [appState?.currency, appDispatch, isLogin, selectedFiat]);

  // 处理小图表数据（收起时显示在右侧，使用最后7天数据）
  const miniChartConfig = useMemo(() => {
    let values = [];
    let labels = [];
    
    // 从传入的数据中取最后7天（收起状态显示）
    const chartDataForMini = propChartData && Array.isArray(propChartData) 
      ? propChartData.slice(-7) 
      : null;
    
    if (chartDataForMini && chartDataForMini.length > 0) {
      // 处理接口返回的数据格式
      values = chartDataForMini.map((item) => {
        if (typeof item === 'number') return item;
        return parseFloat(item.asset || item.value || item.amount || item) || 0;
      });
      labels = chartDataForMini.map((_, index) => index);
    } else {
      // 没有数据时，显示一条值为 0 的水平线
      values = [0, 0];
      labels = [0, 1];
    }

    // 检查是否在浏览器环境中
    let gradient = null;
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      gradient = ctx.createLinearGradient(0, 0, 0, 60);
      gradient.addColorStop(0, "rgba(69, 191, 89, 0.3)");
      gradient.addColorStop(0.5, "rgba(69, 191, 89, 0.1)");
      gradient.addColorStop(1, "rgba(69, 191, 89, 0)");
    }

    return {
      labels,
      datasets: [
        {
          label: "Asset Trend",
          data: values,
          borderColor: "#45BF59",
          backgroundColor: gradient || "rgba(69, 191, 89, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 1.5,
        },
      ],
    };
  }, [propChartData]);

  const miniChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
  };

  if (isAuthChecking) return null;

  if (!isLogin) {
    return (
      <div className={styles["asset-balance"]}>
        <div className={styles["asset-balance__not-login"]}>
          <div className={styles["asset-balance__title"]}>
            {t("home.welcomeToSigma") || "Welcome to Spikex"}
          </div>
          <div className={styles["asset-balance__subtitle"]}>
            {t("home.buySellCryptoInMinutes") || "The AI-Driven Crypto, Forex & Futures Trading Platform"}
          </div>
          <div className={styles["asset-balance__actions"]}>
            <button
              className={`${styles["asset-balance__btn"]} ${styles["asset-balance__btn--primary"]} ${styles["asset-balance__btn--unlogin"]}`}
              onClick={() => {
                window.location.href = getUrl("/accounts/register");
              }}
            >
              {t("home.signUp") || "Sign up"}
            </button>
            <button
              className={`${styles["asset-balance__btn"]} ${styles["asset-balance__btn--secondary"]} ${styles["asset-balance__btn--unlogin"]}`}
              onClick={() => {
                window.location.href = getUrl("/accounts/login");
              }}
            >
              {t("home.logIn") || "Log in"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["asset-balance"]}>
      <div className={styles["asset-balance__header"]}>
        <span className={styles["asset-balance__label"]}>
          {t("home.totalBalance") || "Total Balance"}
        </span>
        <EyeIcon
          isOpen={!hideBalance}
          onClick={() => setHideBalance(!hideBalance)}
        />
      </div>

      <div className={styles["asset-balance__content"]}>
        <div className={styles["asset-balance__left"]}>
          <div className={styles["asset-balance__usdt"]}>
            {hideBalance ? (
              "****"
            ) : (
              <>
                <span className={styles["asset-balance__usdt-amount"]}>
                  {selectedFiat?.toLowerCase() === "usdt"
                    ? (totalUsdtAmount ? Big(totalUsdtAmount).toFixedCy(2) : "0")
                    : (exchangeRate != null ? convertedAmount : "--")}
                </span>
                {fiatSelectable ? (
                  <div className={styles["asset-balance__usdt-unit-wrap"]} ref={fiatDropdownWrapRef}>
                    <button
                      type="button"
                      className={styles["asset-balance__usdt-unit"]}
                      onClick={() => setFiatDropdownOpen((open) => !open)}
                    >
                      <span className={styles["asset-balance__usdt-unit-text"]}>
                        {(selectedFiat || "USDT").toUpperCase()}
                      </span>
                      <span
                        className={`${styles["asset-balance__usdt-unit-icon"]} ${
                          fiatDropdownOpen ? styles["asset-balance__usdt-unit-icon--open"] : ""
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="4" viewBox="0 0 8 4" fill="none">
                        <path d="M8 0H0L4 4L8 0Z" fill="white" fill-opacity="0.4"/>
                        </svg>
                      </span>
                    </button>
                    {fiatDropdownOpen && (
                      <div className={styles["asset-balance__usdt-unit-menu"]}>
                        {fiatOptions.map((code) => (
                          <div
                            key={code}
                            className={`${styles["asset-balance__usdt-unit-item"]} ${
                              selectedFiat === code
                                ? styles["asset-balance__usdt-unit-item--active"]
                                : ""
                            }`}
                            onClick={() => {
                              userHasChosenFiatRef.current = true;
                              const upperCode = (code || "USDT").toUpperCase();
                              const lowerKey = upperCode.toLowerCase();
                              setSelectedFiat(upperCode);
                              Cookie.set("currency", lowerKey);
                              appDispatch({
                                payload: {
                                  currency: lowerKey,
                                },
                              });
                              setFiatDropdownOpen(false);
                            }}
                          >
                            {code.toUpperCase()}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className={styles["asset-balance__usdt-unit"]}>{(selectedFiat || "USDT").toUpperCase()}</span>
                )}
              </>
            )}
          </div>
          <div className={styles["asset-balance__fiat"]}>
            {hideBalance ? (
              "****"
            ) : (
              <span className={styles["asset-balance__fiat-amount"]}>
                {usdRate != null
                  ? `≈$${Big(totalUsdtAmount || "0").times(usdRate).toFixedCy(2)}`
                  : "≈$--"}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className={`${styles["asset-balance__btn"]} ${styles["asset-balance__btn--primary"]}`}
          onClick={() => onOpenDeposit?.(selectedFiat || "USDT")}
        >
          {depositLabel}
        </button>
      </div>

      {/* {!chartExpanded && isLogin && (
        <div className={styles["asset-balance__expand-toggle"]} onClick={onToggleChartExpand}>
          <ExpandIcon expanded={chartExpanded} onClick={onToggleChartExpand} />
        </div>
      )} */}
    </div>
  );
};

export default observer(AssetBalance);
